import { useCallback, useEffect, useRef } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import { LoginRequestDTO } from '@/core/api/dtos/request/auth/login'
import { LoginGoogleRequestDTO } from '@/core/api/dtos/request/auth/loginGoogle'
import { useAuth } from '@/core/hooks/useAuth'

import { LoginPageUI } from './LoginPageUI'

export const LoginPage = () => {
	const { login, loginWithGoogle, isLoading, error: authError } = useAuth()
	const googleLoginAttempted = useRef(false)
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const { t } = useTranslation('auth')

	// Get redirect path from location state (if any)
	const from = location.state?.from || '/dashboard'

	const handleSubmit = async (values: LoginRequestDTO) => {
		await login(values, from)
	}

	// Get the hostname of the current page, using a helper function that applies for dev, local, and production environments
	const getHostname = () => {
		return (
			document.location.protocol +
			'//' +
			document.location.hostname +
			(document.location.port ? ':' + document.location.port : '')
		)
	}

	// Handle Google OAuth callback on /login and /login/google without a separate page
	useEffect(() => {
		const code = searchParams.get('code')

		if (!code || isLoading || googleLoginAttempted.current) {
			return
		}

		// Prevent another execution on immediate re-render
		googleLoginAttempted.current = true

		const payload: LoginGoogleRequestDTO = {
			code,
			scope: searchParams.get('scope') ?? '',
			authuser: searchParams.get('authuser') ?? '',
			hd: searchParams.get('hd') ?? '',
			sessionState:
				searchParams.get('session_state') ??
				searchParams.get('sessionState') ??
				'',
			prompt: searchParams.get('prompt') ?? '',
			redirect: encodeURI(getHostname() + '/login/google'),
		}

		loginWithGoogle(payload, '/dashboard')
	}, [searchParams])

	// is form valid
	const validationSchema = yup.object().shape({
		email: yup
			.string()
			.email(t('register.errors.invalidEmail'))
			.required(
				t('register.errors.required', {
					field: t('register.email').toLowerCase(),
				})
			),
		password: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.password').toLowerCase(),
				})
			)
			.min(8, t('register.errors.passwordMinLength')),
	})

	const form = useForm<LoginRequestDTO, any, LoginRequestDTO>({
		resolver: yupResolver(validationSchema) as any,
		mode: 'onChange',
		reValidateMode: 'onChange',
	})

	const googleClientId = import.meta.env.VITE_GOOGLE_AUTH_CLIENT_ID
	const googleAuthEnabled = Boolean(googleClientId)

	const buildGoogleAuthUrl = useCallback(() => {
		const redirectUri = `${window.location.origin}/login/google`

		const scope = 'openid profile email'
		const responseType = 'code'

		const googleURL = `https://accounts.google.com/o/oauth2/auth?client_id=${encodeURIComponent(
			googleClientId
		)}&redirect_uri=${encodeURIComponent(
			redirectUri
		)}&scope=${encodeURIComponent(
			scope
		)}&response_type=${encodeURIComponent(responseType)}`

		return googleURL
	}, [googleClientId])

	const handleGoogleLogin = () => {
		const url = buildGoogleAuthUrl()
		window.location.href = url
	}

	return (
		<LoginPageUI
			isLoading={isLoading}
			error={authError}
			form={form}
			onFinish={handleSubmit}
			handleGoogleLogin={handleGoogleLogin}
			googleAuthEnabled={googleAuthEnabled}
		/>
	)
}
